<script lang="ts">
  /**
   * レッスン 1 本（lesson-format.md §1、§5、§7）。読む → 実験 → 書く → 暗記事項 → ドリル の順。
   * LessonDoc の木をコンポーネントで描く（{@html} を使わない）。記録は親がコールバックで受け取る。
   */
  import type { WriteAcCard } from '../../lib/cards';
  import InlineMarkdown from '../InlineMarkdown.svelte';
  import type { GradeResult } from '../types';
  import type { CardAnswer } from '../cards/answer';
  import BlockNodes from './BlockNodes.svelte';
  import ExperimentBlock from './ExperimentBlock.svelte';
  import InlineNodes from './InlineNodes.svelte';
  import MemoList from './MemoList.svelte';
  import WriteBlock from './WriteBlock.svelte';
  import { setLessonId } from './lesson-context';
  import type { LessonDoc, MemoCard } from './types';

  interface Props {
    doc: LessonDoc;
    /** 見出しの上に出すユニット名（「Stage 1 入出力と型」） */
    unitLabel?: string | undefined;
    /** front matter の memos を解決したもの（見つからない id は除く） */
    memos?: readonly MemoCard[];
    /** write card="…" のカードを探す */
    findWriteCard?: ((id: string) => WriteAcCard | undefined) | undefined;
    /** このレッスンのドリルに出すカードの枚数（0 ならボタンを出さない） */
    drillCount?: number;
    onexperiment?: ((id: string, matched: boolean) => void) | undefined;
    onwrite?: ((id: string) => void) | undefined;
    oncardgrade?:
      ((card: WriteAcCard, result: GradeResult, answer: CardAnswer) => void) | undefined;
    ondrill?: (() => void) | undefined;
  }

  let {
    doc,
    unitLabel,
    memos = [],
    findWriteCard,
    drillCount = 0,
    onexperiment,
    onwrite,
    oncardgrade,
    ondrill,
  }: Props = $props();

  const uid = $props.id();
  // svelte-ignore state_referenced_locally
  setLessonId(doc.meta.id);
</script>

<article
  class="lesson"
  aria-labelledby="{uid}-title"
  data-testid="lesson"
  data-lesson-id={doc.meta.id}
>
  <header class="lesson-head">
    <span class="eyebrow">レッスン{unitLabel ? ` · ${unitLabel}` : ''}</span>
    <h1 id="{uid}-title" tabindex="-1">{doc.meta.title}</h1>
    <section class="goals" aria-labelledby="{uid}-goals">
      <h2 id="{uid}-goals" class="goals-title">このレッスンでできること</h2>
      <ul>
        {#each doc.meta.goals as g, i (i)}<li><InlineMarkdown text={g} /></li>{/each}
      </ul>
    </section>
  </header>

  {#each doc.explain as sec, i (i)}
    <section class="sec">
      <!-- 節へのディープリンク #/lesson/<id>#<slug> の受け側（problem-format.md §6.3） -->
      <h2 id="sec-{sec.slug}" class="sec-heading" tabindex="-1">
        <InlineNodes nodes={sec.heading} />
      </h2>
      <BlockNodes blocks={sec.blocks} />
    </section>
  {/each}

  {#if doc.experiments.length > 0}
    <section class="sec" aria-labelledby="{uid}-exp">
      <h2 id="{uid}-exp">実験</h2>
      {#each doc.experiments as e (e.block.id)}
        <section class="sub-sec panel" aria-labelledby="{uid}-exp-{e.block.index}">
          <h3 id="{uid}-exp-{e.block.index}">
            <span class="num-label">実験 {e.block.index}</span>
            <InlineNodes nodes={e.title} />
          </h3>
          <BlockNodes blocks={e.intro} />
          <ExperimentBlock block={e.block} oncompared={onexperiment} />
        </section>
      {/each}
    </section>
  {/if}

  {#if doc.writes.length > 0}
    <section class="sec" aria-labelledby="{uid}-write">
      <h2 id="{uid}-write">書く</h2>
      {#each doc.writes as w (w.block.id)}
        <section class="sub-sec" aria-labelledby="{uid}-write-{w.block.index}">
          <h3 id="{uid}-write-{w.block.index}">
            <span class="num-label">書く課題 {w.block.index}</span>
            <InlineNodes nodes={w.title} />
          </h3>
          <BlockNodes blocks={w.intro} />
          <WriteBlock
            block={w.block}
            card={'card' in w.block ? findWriteCard?.(w.block.card) : undefined}
            ondone={onwrite}
            {oncardgrade}
          />
        </section>
      {/each}
    </section>
  {/if}

  {#if memos.length > 0}
    <section class="sec" aria-labelledby="{uid}-memo">
      <h2 id="{uid}-memo">暗記事項</h2>
      <MemoList {memos} />
    </section>
  {/if}

  {#if drillCount > 0}
    <section class="sec drill panel" aria-labelledby="{uid}-drill">
      <h2 id="{uid}-drill">このレッスンのドリル</h2>
      <p class="text-soft">{drillCount} 枚を順に出します。答えは今日の記録に入ります。</p>
      <div>
        <button
          class="btn btn-primary"
          type="button"
          data-testid="lesson-drill"
          onclick={() => ondrill?.()}>ドリルを始める</button
        >
      </div>
    </section>
  {/if}
</article>

<style>
  .lesson {
    display: flex;
    flex-direction: column;
    gap: var(--space-6);
    min-width: 0;
    line-height: var(--lh-body);
  }
  .lesson-head {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .lesson-head h1 {
    font-size: var(--fs-2xl);
    line-height: var(--lh-tight);
  }
  .goals {
    margin-top: var(--space-2);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-md);
    background: var(--accent-soft);
  }
  .goals-title {
    font-size: var(--fs-sm);
    color: var(--accent-soft-text);
    margin-bottom: var(--space-1);
  }
  .goals ul,
  .sec {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    min-width: 0;
  }
  .sec > h2 {
    font-size: var(--fs-xl);
    padding-bottom: var(--space-2);
    border-bottom: 1px solid var(--border);
    margin-bottom: var(--space-2);
  }
  .sec-heading {
    scroll-margin-top: calc(var(--scoreboard-h) + var(--space-3));
  }
  .sub-sec {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    min-width: 0;
    margin-bottom: var(--space-4);
  }
  .sub-sec h3 {
    font-size: var(--fs-lg);
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--space-2);
  }
  .num-label {
    font-size: var(--fs-sm);
    color: var(--accent-soft-text);
    background: var(--accent-soft);
    padding: 0 var(--space-2);
    border-radius: var(--radius-xs);
    font-weight: var(--fw-semibold);
  }
  .drill {
    gap: var(--space-3);
  }
  .drill > h2 {
    border-bottom: 0;
    padding-bottom: 0;
    margin-bottom: 0;
  }
</style>
